/**
 * Transcript stand-in for a form that is currently open as a sheet.
 *
 * Deliberately NOT a second copy of the form: the card owns its wizard step,
 * field values and validation in local state, so two mounted copies would
 * diverge the moment the visitor typed. This is one line and one affordance.
 *
 * @module components/chat/bubble/form-opened-placeholder
 */
import { ClipboardList } from 'lucide-react'
import { getChromeStrings, getEmbedLocale } from '@/lib/embed-locale'

export interface FormOpenedPlaceholderProps {
  /** Same title the sheet shows, so the transcript row reads as its shadow. */
  title: string
  /** Whether the sheet is currently showing. Drives the caption and the button. */
  isOpen: boolean
  /** Brings the sheet back after the visitor closed it. */
  onReopen: () => void
}

/**
 * Compact transcript row standing in for the form while it lives in the sheet.
 *
 * While the sheet is open the row is a label only — a "reopen" button for an
 * already-open sheet is a button that does nothing. Once the visitor closes the
 * sheet the row becomes their way back in, with everything they typed still in
 * the (hidden, still-mounted) card.
 */
export function FormOpenedPlaceholder({ title, isOpen, onReopen }: FormOpenedPlaceholderProps) {
  const strings = getChromeStrings(getEmbedLocale() ?? 'en')

  return (
    <div
      data-testid="bubble-form-placeholder"
      className="mx-auto flex w-full max-w-2xl items-center gap-2 rounded-xl border border-dashed border-border bg-muted/30 px-3 py-2"
    >
      <ClipboardList className="size-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[12.5px] font-medium text-foreground">{title}</p>
        <p className="text-[11.5px] text-muted-foreground">
          {isOpen ? strings.formOpened : strings.formClosed}
        </p>
      </div>
      {!isOpen && (
        <button
          type="button"
          data-testid="bubble-form-reopen"
          onClick={onReopen}
          className="shrink-0 rounded-md px-2 py-1 text-[12px] font-medium text-primary transition-colors hover:bg-primary/10"
        >
          {strings.reopenForm}
        </button>
      )}
    </div>
  )
}
